import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  ShieldCheck,
  FileText,
  BookOpen,
  Sparkles,
  CheckCircle2,
  Circle,
  ArrowRight,
  Lock,
  LogOut,
  Building2,
  Bell,
  Clock,
  AlertTriangle,
  Eye,
  Download,
  FileDown,
  Trash2,
  Copy,
  ExternalLink,
  TrendingUp,
} from "lucide-react";
import SEO from "@/components/SEO";
import DeepScanCard from "@/components/DeepScanCard";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMP_STYLES = {
  alta: "bg-rose-100 text-rose-800 border-rose-200",
  media: "bg-amber-100 text-amber-800 border-amber-200",
  baja: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function Cuenta() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [proInfo, setProInfo] = useState(null);
  const [novedades, setNovedades] = useState([]);
  const [misDocs, setMisDocs] = useState([]);
  const [misScans, setMisScans] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const refreshDocs = () => axios.get(`${API}/me/documents`).then(({ data }) => setMisDocs(data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/pro/info`).then(({ data }) => setProInfo(data)).catch(() => {});
    refreshDocs();
    axios.get(`${API}/me/scans`).then(({ data }) => setMisScans(data)).catch(() => {});
    axios.get(`${API}/novedades`).then(({ data }) => setNovedades(data)).catch(() => {});
  }, []);

  // Auto-trigger checkout cuando venimos desde Register/Home con ?checkout=1
  useEffect(() => {
    if (!user) return;
    if (searchParams.get("checkout") !== "1") return;
    if (user.es_pro) return;
    // Delay corto para asegurar que el estado de proInfo esté cargado.
    const t = setTimeout(() => { handleCheckout(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, searchParams]);

  // Manejo de retorno desde Mercado Pago
  useEffect(() => {
    const pago = searchParams.get("pago");
    if (!pago) return;
    if (pago === "exito") {
      toast.success("¡Pago recibido! Confirmando tu acceso Pro…", { duration: 4000 });
      trackEvent("checkout_returned", { status: "exito" });
      // Polling: el webhook puede tardar unos segundos en otorgar el Pro
      let intentos = 0;
      const maxIntentos = 12; // ~24s
      const interval = setInterval(async () => {
        intentos += 1;
        const u = await refresh();
        if (u?.es_pro) {
          clearInterval(interval);
          toast.success("✓ Tu acceso Pro está activo — permanente", { duration: 5000 });
          trackEvent("checkout_completed", {
            value: 14990,
            currency: "CLP",
          });
        } else if (intentos >= maxIntentos) {
          clearInterval(interval);
          toast.info("Tu pago se está procesando. Te avisaremos por email cuando se active.", { duration: 6000 });
        }
      }, 2000);
    } else if (pago === "fallo") {
      toast.error("El pago no se completó. Puedes intentarlo de nuevo cuando quieras.");
      trackEvent("checkout_returned", { status: "fallo" });
    } else if (pago === "pendiente") {
      toast.info("Tu pago quedó pendiente. Te avisaremos por email cuando se confirme.");
      trackEvent("checkout_returned", { status: "pendiente" });
    } else if (pago === "cancelado") {
      toast.info("Cancelaste el pago. Cuando quieras retomás desde acá.");
      trackEvent("checkout_returned", { status: "cancelado" });
    }
    // Limpia los query params para no re-disparar al refrescar
    searchParams.delete("pago");
    searchParams.delete("checkout");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, refresh]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    trackEvent("checkout_initiated", {
      value: 14990,
      currency: "CLP",
      product: "pro_pago_unico_permanente",
      gateway: "transbank",
    });
    try {
      const { data } = await axios.post(`${API}/pro/checkout/transbank`);
      if (data?.buy_order === "ALREADY_PRO") {
        toast.info("Ya tienes acceso Pro permanente.");
        await refresh();
        return;
      }
      if (data?.url && data?.token) {
        // POST-form a Webpay con token_ws
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.url;
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "token_ws";
        input.value = data.token;
        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
        return;
      }
      toast.error("Webpay no devolvió el link. Intenta de nuevo.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "El pago no está disponible en este momento.";
      toast.error(detail);
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleRetryDoc = async (docId) => {
    try {
      await axios.post(`${API}/documents/${docId}/generate`);
      toast.success("Regenerando…");
      trackEvent("doc_retry", { doc_id: docId });
      await refreshDocs();
    } catch (e) {
      const detail = e?.response?.data?.detail || "No pudimos reintentar. Prueba de nuevo.";
      toast.error(typeof detail === "string" ? detail : "Error al reintentar");
    }
  };

  if (!user) return null;

  const onboardingDone = user.onboarding_completo;
  const isSuperAdmin = user.role === "superadmin";

  return (
    <>
      <SEO title="Mi cuenta" description="Tu panel Ley de Datos Chile" path="/cuenta" noindex />
      <section className="px-6 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto">
          {/* Header — operating dashboard style */}
          <div className="mb-8 border-b border-slate-200 pb-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-3">
                  <span className="live-dot inline-block" /> Cuenta · activa
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-slate-900 tracking-tight leading-tight">
                  Hola{user.razon_social ? `, ${user.razon_social}` : ""}
                </h1>
                <p className="mt-1 text-sm font-mono text-slate-500">{user.email}</p>
              </div>
              <div className="flex items-center gap-2">
                {isSuperAdmin && (
                  <Link
                    to="/admin"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium"
                  >
                    Super Admin
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700"
                >
                  <LogOut className="w-4 h-4" />
                  Cerrar sesión
                </button>
              </div>
            </div>

            {/* KPI strip estilo operating dashboard */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
              <KpiTile
                label="Estado Pro"
                value={user.es_pro ? "ACTIVO" : "FREE"}
                tone={user.es_pro ? "live" : "muted"}
                hint={user.es_pro ? (user.es_pro_permanente ? "Permanente · sin cobros futuros" : `${user.dias_pro_restantes || 0} días restantes`) : "Activa Pro · $14.990"}
              />
              <KpiTile
                label="Onboarding"
                value={onboardingDone ? "100%" : "Pendiente"}
                tone={onboardingDone ? "live" : "warn"}
                hint={onboardingDone ? "Datos listos" : "Empieza ahora"}
              />
              <KpiTile
                label="Documentos"
                value={String(misDocs.length)}
                tone="info"
                hint="generados"
              />
              <KpiTile
                label="Escaneos"
                value={String(misScans.length)}
                tone="info"
                hint="historial completo"
              />
            </div>
          </div>

          {/* Pro state */}
          <ProStateCard user={user} proInfo={proInfo} onCheckout={handleCheckout} loading={checkoutLoading} />

          {/* Onboarding + Generar (Generar solo si es Pro) */}
          <div className={`grid grid-cols-1 ${user.es_pro ? "md:grid-cols-2" : ""} gap-5 mb-10`}>
            <TaskCard
              icon={Building2}
              title="1. Completa el onboarding de tu empresa"
              description="Una sola entrevista que se reusa para todos tus documentos."
              done={onboardingDone}
              ctaText={onboardingDone ? "Editar respuestas" : "Empezar onboarding"}
              ctaLink="/onboarding"
              testId="task-onboarding"
            />
            {user.es_pro && (
              <TaskCard
                icon={FileText}
                title="2. Genera tus documentos"
                description={onboardingDone ? "Tus datos ya están listos. Generar es directo." : "Después del onboarding, los documentos se generan sin volver a preguntarte lo mismo."}
                done={false}
                disabled={!onboardingDone}
                ctaText="Generar documento"
                ctaLink="/cuenta/generar"
                testId="task-documents"
              />
            )}
          </div>

          {/* Novedades */}
          <section className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight inline-flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#1E3A8A]" />
                Novedades de la Ley 21.719
              </h2>
              <span className="text-xs text-slate-500">{novedades.length} {novedades.length === 1 ? "novedad" : "novedades"}</span>
            </div>
            {novedades.length === 0 ? (
              <div data-testid="novedades-empty" className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
                <p className="text-slate-600">Aún no hay novedades publicadas. Te avisaremos por email cuando salga la primera.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="novedades-list">
                {novedades.slice(0, 5).map((n) => (
                  <article key={n.id} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide border ${IMP_STYLES[n.importancia] || IMP_STYLES.media}`}>
                            {n.importancia === "alta" && <AlertTriangle className="w-3 h-3" />}
                            Importancia {n.importancia}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(n.fecha_publicacion).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" })}
                          </span>
                        </div>
                        <h3 className="font-display text-lg font-semibold text-slate-900">{n.titulo}</h3>
                        <p className="mt-2 text-sm text-slate-600 leading-relaxed">{n.resumen}</p>
                        {n.accion_recomendada && (
                          <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
                            <strong className="text-slate-900">Acción recomendada:</strong> <span className="text-slate-700">{n.accion_recomendada}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>

          {/* Mis documentos */}
          <section className="mb-10">
            <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight mb-5 inline-flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1E3A8A]" />
              Mis documentos
            </h2>
            {user.es_pro && <ComplianceDashboard docs={misDocs} scans={misScans} />}
            <AutoGenStatusBanner docs={misDocs} user={user} onRefresh={refreshDocs} onCheckout={handleCheckout} />
            {misDocs.length === 0 ? (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm mb-4">
                  {onboardingDone
                    ? "Aún no se han generado documentos. Tras el onboarding completo deberían aparecer aquí automáticamente."
                    : "Completa el onboarding y generamos automáticamente tus 3 documentos esenciales."}
                </p>
                {!onboardingDone && (
                  <Link
                    to="/onboarding"
                    data-testid="empty-docs-onboarding-cta"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] hover:text-[#1E40AF]"
                  >
                    Empezar onboarding
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            ) : (
              <DocumentList docs={misDocs} onChange={refreshDocs} isPro={user.es_pro} onCheckout={handleCheckout} onRetry={handleRetryDoc} />
            )}
          </section>

          {/* Mis escaneos + evolución por dominio (Pro: re-scans sin contar) */}
          <ScanHistorySection user={user} />

          {/* Deep scan Pro — escanea hasta 5 páginas internas */}
          <DeepScanCard isPro={user.es_pro || user.role === "superadmin"} onCheckout={handleCheckout} />

          {/* Pro gated cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ProGatedCard icon={BookOpen} title="Guías de implementación" description="Paso a paso para configurar consentimiento de cookies y bloqueo de scripts según tu plataforma." link="/guias" isPro={user.es_pro} testId="card-guias" />
            <ProGatedCard icon={Sparkles} title="Descarga de PDFs" description="Los documentos se previsualizan gratis. Descargar el PDF completo requiere acceso Pro." link="/cuenta/generar" isPro={user.es_pro} testId="card-downloads" />
          </div>
        </div>
      </section>
    </>
  );
}

function ProStateCard({ user, proInfo, onCheckout, loading }) {
  const precio = proInfo?.precio_clp || 14990;
  const tbkDisponible = proInfo?.transbank_disponible;
  const esPermanente = !!user?.es_pro_permanente;

  // Pro activo
  if (user.es_pro) {
    return (
      <div data-testid="pro-active-banner" className="mb-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <div>
            <p className="text-xs font-bold tracking-wider uppercase text-white/80">Pro activo</p>
            <h2 className="font-display text-xl font-semibold">
              {esPermanente ? "Acceso permanente" : `Te quedan ${user.dias_pro_restantes} ${user.dias_pro_restantes === 1 ? "día" : "días"}`}
            </h2>
          </div>
        </div>
        <p className="mt-3 text-white/90 text-sm">
          {esPermanente
            ? "Compraste el acceso Pro con un pago único. Sin cobros futuros, sin renovación. Tus documentos y las alertas de la ley son tuyos para siempre."
            : `Acceso vigente hasta el ${user.acceso_pro_hasta ? new Date(user.acceso_pro_hasta).toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" }) : "—"}.`}
        </p>
      </div>
    );
  }

  // Expirado o nunca pagó
  const expirado = !!user.acceso_pro_hasta && !user.es_pro;

  return (
    <div data-testid="pro-upgrade-card" className="mb-8 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1E3A8A] via-[#1E40AF] to-[#0F172A] text-white p-8 sm:p-10">
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/5 blur-3xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold uppercase tracking-wide">
          <Sparkles className="w-3.5 h-3.5" />
          {expirado ? "Acceso expirado" : "Acceso Pro"}
        </div>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight">
          Desbloquea todo con un pago único
        </h2>
        <div className="mt-4 flex items-baseline gap-2 flex-wrap">
          <span className="font-display text-4xl sm:text-5xl font-bold">${precio.toLocaleString("es-CL")}</span>
          <span className="text-lg text-white/80">CLP</span>
          <span className="ml-3 text-sm text-white/70">Pago único · acceso permanente</span>
        </div>

        <p className="mt-5 text-sm text-white/80 max-w-xl">
          Sin suscripciones, sin cobros futuros, sin letra chica. Pagas una sola vez $14.990 CLP y accedes
          para siempre a los 7 documentos, escaneos ilimitados y alertas de la ley cuando cambie.
        </p>

        <ul className="mt-5 space-y-2 max-w-xl">
          {(proInfo?.incluye || []).map((it, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white/90">
              <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{it}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap gap-3 items-center">
          <button
            data-testid="pro-checkout-btn"
            onClick={onCheckout}
            disabled={loading || !tbkDisponible}
            className="h-12 px-7 rounded-lg bg-white text-[#1E3A8A] font-semibold hover:bg-slate-100 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed transition-all inline-flex items-center gap-2"
          >
            {!tbkDisponible ? (<><Lock className="w-4 h-4" /> Webpay — Próximamente</>) : loading ? "Iniciando pago…" : (<>Desbloquear todo — $14.990 <ArrowRight className="w-4 h-4" /></>)}
          </button>
          {!tbkDisponible && (
            <p className="text-xs text-white/70">Te avisaremos por email apenas Webpay esté habilitado.</p>
          )}
        </div>
        <p className="mt-3 text-[11px] text-white/60">Pago único · Webpay · sin renovación · alertas de la ley gratis de por vida.</p>
      </div>
    </div>
  );
}

function TaskCard({ icon: Icon, title, description, done, disabled, ctaText, ctaLink, testId }) {
  return (
    <div data-testid={testId} className={`p-6 rounded-2xl border-2 ${done ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-slate-200"}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-[#1E3A8A]/10 text-[#1E3A8A]"}`}>
          {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">{description}</p>
          {disabled ? (
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-400">
              <Circle className="w-3.5 h-3.5" />
              Completa el onboarding primero
            </span>
          ) : (
            <Link to={ctaLink} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] hover:underline">
              {ctaText}
              <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

function ProGatedCard({ icon: Icon, title, description, link, isPro, testId }) {
  return (
    <div data-testid={testId} className="p-6 rounded-2xl border-2 border-slate-200 bg-white">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 text-[#1E3A8A] grid place-items-center shrink-0"><Icon className="w-5 h-5" /></div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
            {!isPro && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                <Lock className="w-3 h-3" /> Pro
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600 leading-relaxed">{description}</p>
          {isPro ? (
            <Link to={link} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A8A] hover:underline">
              Acceder
              <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-400">
              <Lock className="w-3.5 h-3.5" />
              Requiere Pro
            </span>
          )}
        </div>
      </div>
    </div>
  );
}


// Mapeo legible de tipos → nombre humano
const TIPO_NOMBRES = {
  politica_privacidad: "Política de Privacidad",
  clausula_consentimiento: "Cláusula de Consentimiento",
  dpa: "Contrato con Encargado (DPA)",
  rat: "Catálogo de Datos Personales (RAT)",
};

async function downloadDocAPI(docId, format, nombre) {
  try {
    const url = format === "pdf" ? `${API}/documents/${docId}/pdf` : `${API}/documents/${docId}/download?format=${format}`;
    const res = await axios.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/octet-stream" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    const slug = (nombre || "documento").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    a.download = `${slug}-leydedatoschile.${format}`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    toast.success(`Descargando ${format.toUpperCase()}…`);
    trackEvent("document_downloaded", { format, doc_nombre: nombre || "documento" });
  } catch (e) {
    const msg = e?.response?.data?.detail || "No pudimos descargar el documento";
    toast.error(typeof msg === "string" ? msg : "Error al descargar");
  }
}

function DocumentList({ docs, onChange, isPro, onCheckout, onRetry }) {
  const navigate = useNavigate();
  const [confirmId, setConfirmId] = useState(null);

  const handleView = (docId) => navigate(`/cuenta/generar?ver=${docId}`);

  const handleCopy = async (docId, nombre) => {
    if (!isPro) {
      toast.error("Activa Pro para copiar el contenido completo");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/documents/${docId}`);
      await navigator.clipboard.writeText(data.contenido || "");
      toast.success(`${nombre} copiado al portapapeles`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No pudimos copiar el documento";
      toast.error(typeof msg === "string" ? msg : "Error al copiar");
    }
  };

  const handleDownload = (docId, fmt, nombre) => {
    if (!isPro) {
      onCheckout?.();
      return;
    }
    downloadDocAPI(docId, fmt, nombre);
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success("Documento eliminado");
      setConfirmId(null);
      onChange?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "No pudimos eliminar el documento";
      toast.error(typeof msg === "string" ? msg : "Error al eliminar");
    }
  };

  const handleRetry = async (docId) => {
    if (onRetry) {
      await onRetry(docId);
      return;
    }
    try {
      await axios.post(`${API}/documents/${docId}/generate`);
      toast.success("Regenerando…");
      onChange?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "No pudimos reintentar. Vuelve a intentarlo.";
      toast.error(typeof detail === "string" ? detail : "Error al reintentar");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
      {/* Desktop table */}
      <table className="w-full hidden md:table">
        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
          <tr>
            <th className="text-left p-4">Documento</th>
            <th className="text-left p-4">Estado</th>
            <th className="text-left p-4">Generado</th>
            <th className="text-right p-4">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {docs.slice(0, 20).map((d) => {
            const nombre = TIPO_NOMBRES[d.tipo] || d.tipo.replace(/_/g, " ");
            const isGenerated = d.estado === "generado";
            const isAuto = d.auto_generado === true;
            const defaultsCount = Array.isArray(d.defaults_inferidos) ? d.defaults_inferidos.length : 0;
            const showDefaultsBadge = isPro && isAuto && defaultsCount > 0;
            return (
              <tr key={d.id} data-testid={`doc-row-${d.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="p-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-slate-900">{nombre}</span>
                    {isAuto && (
                      <span className="font-mono text-[9px] tracking-wider uppercase text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                        Auto
                      </span>
                    )}
                    {showDefaultsBadge && (
                      <span
                        data-testid={`doc-defaults-badge-${d.id}`}
                        title={`Campos con default inferido: ${(d.defaults_inferidos || []).join(", ")}. Revísalos antes de publicar.`}
                        className="font-mono text-[9px] tracking-wider uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded cursor-help inline-flex items-center gap-1"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        {defaultsCount} defaults
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{d.id.slice(0, 8)}</div>
                </td>
                <td className="p-4">
                  <StateBadge estado={d.estado} />
                </td>
                <td className="p-4 text-sm text-slate-500">
                  {d.generado_en ? new Date(d.generado_en).toLocaleString("es-CL") : "—"}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-1">
                    {isGenerated ? (
                      <>
                        <IconBtn
                          icon={Eye}
                          label="Ver documento"
                          onClick={() => handleView(d.id)}
                          testId={`doc-view-${d.id}`}
                        />
                        {isPro ? (
                          <>
                            <IconBtn
                              icon={Copy}
                              label="Copiar texto"
                              onClick={() => handleCopy(d.id, nombre)}
                              testId={`doc-copy-${d.id}`}
                            />
                            <IconBtn
                              icon={Download}
                              label="Descargar Markdown (.md)"
                              onClick={() => handleDownload(d.id, "md", nombre)}
                              testId={`doc-download-md-${d.id}`}
                            />
                            <IconBtn
                              icon={FileDown}
                              label="Descargar PDF profesional"
                              onClick={() => handleDownload(d.id, "pdf", nombre)}
                              testId={`doc-download-pdf-${d.id}`}
                              primary
                            />
                          </>
                        ) : (
                          <button
                            onClick={onCheckout}
                            data-testid={`doc-unlock-${d.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors"
                          >
                            <Lock className="w-3 h-3" />
                            Desbloquear
                          </button>
                        )}
                      </>
                    ) : d.estado === "error" ? (
                      <button
                        onClick={() => handleRetry(d.id)}
                        data-testid={`doc-retry-${d.id}`}
                        title={d.error_message ? `Error: ${d.error_message}` : "Toca para reintentar"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-50 border border-amber-200 transition"
                      >
                        Reintentar
                      </button>
                    ) : d.estado === "generando" ? (
                      <span data-testid={`doc-generating-${d.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500">
                        <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        Generando…
                      </span>
                    ) : (
                      <Link
                        to={`/cuenta/generar?retomar=${d.id}`}
                        data-testid={`doc-continue-${d.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-[#1E3A8A] hover:bg-[#1E3A8A]/5 transition"
                      >
                        Continuar
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                    <IconBtn
                      icon={Trash2}
                      label="Eliminar"
                      onClick={() => setConfirmId(d.id)}
                      testId={`doc-delete-${d.id}`}
                      danger
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-slate-100">
        {docs.slice(0, 20).map((d) => {
          const nombre = TIPO_NOMBRES[d.tipo] || d.tipo.replace(/_/g, " ");
          const isGenerated = d.estado === "generado";
          const isAuto = d.auto_generado === true;
          const defaultsCount = Array.isArray(d.defaults_inferidos) ? d.defaults_inferidos.length : 0;
          const showDefaultsBadge = isPro && isAuto && defaultsCount > 0;
          return (
            <div key={d.id} className="p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-slate-900 text-sm truncate">{nombre}</span>
                    {isAuto && (
                      <span className="font-mono text-[9px] tracking-wider uppercase text-blue-700 bg-blue-50 border border-blue-100 px-1.5 py-0.5 rounded">
                        Auto
                      </span>
                    )}
                    {showDefaultsBadge && (
                      <span
                        title={`Campos con default inferido: ${(d.defaults_inferidos || []).join(", ")}`}
                        className="font-mono text-[9px] tracking-wider uppercase text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded inline-flex items-center gap-1"
                      >
                        <AlertTriangle className="w-2.5 h-2.5" />
                        {defaultsCount}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {d.generado_en ? new Date(d.generado_en).toLocaleDateString("es-CL") : "—"}
                  </div>
                </div>
                <StateBadge estado={d.estado} />
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {isGenerated ? (
                  <>
                    <SmallBtn onClick={() => handleView(d.id)} icon={Eye}>Ver</SmallBtn>
                    {isPro ? (
                      <>
                        <SmallBtn onClick={() => handleDownload(d.id, "pdf", nombre)} icon={FileDown} primary>PDF</SmallBtn>
                        <SmallBtn onClick={() => handleDownload(d.id, "md", nombre)} icon={Download}>.md</SmallBtn>
                        <SmallBtn onClick={() => handleCopy(d.id, nombre)} icon={Copy}>Copiar</SmallBtn>
                      </>
                    ) : (
                      <button onClick={onCheckout} data-testid={`doc-unlock-m-${d.id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-slate-900 text-white">
                        <Lock className="w-3 h-3" /> Desbloquear
                      </button>
                    )}
                  </>
                ) : d.estado === "error" ? (
                  <button onClick={() => handleRetry(d.id)} data-testid={`doc-retry-m-${d.id}`} title={d.error_message ? `Error: ${d.error_message}` : "Toca para reintentar"} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-amber-700 border border-amber-200">
                    Reintentar
                  </button>
                ) : d.estado === "generando" ? (
                  <span data-testid={`doc-generating-m-${d.id}`} className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /> Generando…
                  </span>
                ) : (
                  <Link to={`/cuenta/generar?retomar=${d.id}`} className="text-xs font-semibold text-[#1E3A8A] inline-flex items-center gap-1">
                    Continuar →
                  </Link>
                )}
                <SmallBtn onClick={() => setConfirmId(d.id)} icon={Trash2} danger>Eliminar</SmallBtn>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm delete modal */}
      {confirmId && (
        <div
          data-testid="confirm-delete-modal"
          className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          onClick={() => setConfirmId(null)}
        >
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-rose-50 grid place-items-center">
                <Trash2 className="w-5 h-5 text-rose-600" />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">¿Eliminar documento?</h3>
            </div>
            <p className="text-sm text-slate-600 mb-5">
              Esto borra el documento permanentemente. No podrás recuperarlo. Puedes generar uno nuevo cuando quieras.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                data-testid="confirm-delete-cancel"
                onClick={() => setConfirmId(null)}
                className="h-10 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm font-medium text-slate-700"
              >
                Cancelar
              </button>
              <button
                data-testid="confirm-delete-confirm"
                onClick={() => handleDelete(confirmId)}
                className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StateBadge({ estado }) {
  const map = {
    generado: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Generado" },
    generando: { bg: "bg-blue-50", text: "text-blue-700", label: "Generando…" },
    borrador: { bg: "bg-slate-100", text: "text-slate-600", label: "Borrador" },
    error: { bg: "bg-rose-50", text: "text-rose-700", label: "Error" },
  };
  const s = map[estado] || map.borrador;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function IconBtn({ icon: Icon, label, onClick, testId, primary, danger }) {
  const base = "w-8 h-8 rounded-lg grid place-items-center transition-colors";
  const tone = primary
    ? "bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
    : danger
    ? "text-rose-500 hover:bg-rose-50 hover:text-rose-700"
    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900";
  return (
    <button type="button" onClick={onClick} data-testid={testId} aria-label={label} title={label} className={`${base} ${tone}`}>
      <Icon className="w-4 h-4" />
    </button>
  );
}

function SmallBtn({ icon: Icon, onClick, children, primary, danger }) {
  const base = "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium";
  const tone = primary
    ? "bg-[#1E3A8A] text-white"
    : danger
    ? "text-rose-600 hover:bg-rose-50"
    : "text-slate-600 hover:bg-slate-100";
  return (
    <button type="button" onClick={onClick} className={`${base} ${tone}`}>
      <Icon className="w-3.5 h-3.5" />
      {children}
    </button>
  );
}

function ComplianceDashboard({ docs, scans }) {
  const generated = docs.filter((d) => d.estado === "generado");
  const generatedTypes = new Set(generated.map((d) => d.tipo));
  const allTypes = [
    { id: "politica_privacidad", name: "Política de Privacidad", critical: true },
    { id: "clausula_consentimiento", name: "Cláusula de Consentimiento", critical: false },
    { id: "dpa", name: "Contrato con Encargado (DPA)", critical: false },
    { id: "rat", name: "Catálogo de Datos Personales (RAT)", critical: true },
  ];
  const missing = allTypes.filter((t) => !generatedTypes.has(t.id));
  const completionPct = Math.round((generatedTypes.size / allTypes.length) * 100);
  const lastScan = scans?.[0];

  let color = "#991B1B";
  if (completionPct >= 75) color = "#166534";
  else if (completionPct >= 40) color = "#92400E";

  return (
    <div data-testid="compliance-dashboard" className="mb-5 bg-gradient-to-br from-white to-slate-50 border border-slate-200 rounded-2xl p-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 flex flex-col items-start">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" />
            Tu cumplimiento
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-display text-5xl font-bold" style={{ color }}>{completionPct}</span>
            <span className="text-slate-500 text-sm">/ 100</span>
          </div>
          <div className="text-sm text-slate-600 mt-1">
            {generatedTypes.size} de {allTypes.length} documentos generados
          </div>
          {lastScan && (
            <div className="mt-3 text-xs text-slate-500">
              Último escaneo: <strong style={{ color: lastScan.score >= 80 ? "#166534" : lastScan.score >= 50 ? "#92400E" : "#991B1B" }}>{lastScan.score}/100</strong>
              <div className="text-slate-400">{new Date(lastScan.creado_en).toLocaleDateString("es-CL")}</div>
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
            {missing.length > 0 ? "Documentos que aún te faltan" : "Cobertura completa"}
          </div>
          {missing.length === 0 ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
              <CheckCircle2 className="w-5 h-5 inline mr-1.5 -mt-0.5" />
              Tienes los 4 documentos clave generados. Recuerda actualizarlos al menos una vez al año.
            </div>
          ) : (
            <div className="space-y-2">
              {missing.map((t) => (
                <Link
                  key={t.id}
                  to={`/cuenta/generar?tipo=${t.id}`}
                  data-testid={`missing-doc-${t.id}`}
                  className="group flex items-center gap-3 bg-white border border-slate-200 rounded-xl p-3.5 hover:border-[#1E3A8A] hover:shadow-sm transition-all"
                >
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${t.critical ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                    <AlertTriangle className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 text-sm">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.critical ? "Obligatorio bajo la Ley 21.719" : "Recomendado"}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-all shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function ScanHistorySection({ user }) {
  const [data, setData] = useState({ dominios: [], total_scans: 0 });
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const reload = () => {
    setLoading(true);
    axios
      .get(`${API}/me/scans/history`)
      .then(({ data }) => {
        setData(data);
        if (data.dominios?.length && !active) setActive(data.dominios[0].dominio);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || data.total_scans === 0) return null;

  const dominio = data.dominios.find((d) => d.dominio === active) || data.dominios[0];

  return (
    <section className="mb-10" data-testid="scan-history-section">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight inline-flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#1E3A8A]" />
          Evolución de tu cumplimiento
        </h2>
        {user.es_pro && (
          <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 font-semibold uppercase tracking-wider">
            Pro · re-escaneos ilimitados
          </span>
        )}
      </div>

      {!user.es_pro && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Con el Módulo Pro puedes re-escanear este dominio cuantas veces quieras sin agotar tu cuota gratuita y ver la evolución a lo largo del tiempo.
        </div>
      )}

      {data.dominios.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {data.dominios.map((d) => (
            <button
              key={d.dominio}
              data-testid={`history-domain-${d.dominio}`}
              onClick={() => setActive(d.dominio)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition ${
                d.dominio === active
                  ? "bg-[#1E3A8A] border-[#1E3A8A] text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-[#1E3A8A]"
              }`}
            >
              {d.dominio} <span className="opacity-70">· {d.puntos.length}</span>
            </button>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex items-baseline gap-2 mb-1">
          <p className="font-display text-lg font-semibold text-slate-900">{dominio.dominio}</p>
          <span className="text-xs text-slate-500">· {dominio.puntos.length} escaneo{dominio.puntos.length === 1 ? "" : "s"}</span>
        </div>
        <ScoreChart points={dominio.puntos} />
        <ScanList puntos={dominio.puntos} />
      </div>
    </section>
  );
}

function ScoreChart({ points }) {
  if (!points || points.length === 0) return null;
  const W = 800;
  const H = 180;
  const PAD = 24;
  const innerW = W - PAD * 2;
  const innerH = H - PAD * 2;
  const n = points.length;
  const xFor = (i) => PAD + (n === 1 ? innerW / 2 : (i * innerW) / (n - 1));
  const yFor = (s) => PAD + innerH - (Math.max(0, Math.min(100, s)) / 100) * innerH;
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"} ${xFor(i).toFixed(1)} ${yFor(p.score).toFixed(1)}`)
    .join(" ");

  return (
    <div data-testid="score-chart" className="mt-3 overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 300 }}>
        {/* Grid lines */}
        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line x1={PAD} x2={W - PAD} y1={yFor(v)} y2={yFor(v)} stroke="#E2E8F0" strokeDasharray="2 4" />
            <text x={6} y={yFor(v) + 4} fontSize="10" fill="#94A3B8">{v}</text>
          </g>
        ))}
        {/* Line */}
        <path d={path} stroke="#1E3A8A" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        {/* Dots */}
        {points.map((p, i) => {
          const color = p.score >= 70 ? "#10B981" : p.score >= 40 ? "#F59E0B" : "#EF4444";
          return (
            <g key={i}>
              <circle cx={xFor(i)} cy={yFor(p.score)} r="5" fill={color} stroke="white" strokeWidth="2" />
              <text x={xFor(i)} y={yFor(p.score) - 10} textAnchor="middle" fontSize="11" fontWeight="600" fill="#0F172A">{p.score}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ScanList({ puntos }) {
  if (!puntos || puntos.length === 0) return null;
  const ordered = [...puntos].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  return (
    <div className="mt-4 divide-y divide-slate-100 text-sm">
      {ordered.slice(0, 6).map((p, i) => (
        <div key={p.scan_id} className="flex items-center justify-between py-2.5">
          <div>
            <p className="text-slate-700">{new Date(p.fecha).toLocaleDateString("es-CL", { day: "numeric", month: "short", year: "numeric" })}</p>
            {i === 0 && <p className="text-[10px] uppercase tracking-wider font-semibold text-emerald-700">Último</p>}
          </div>
          <span className="font-display text-lg font-bold" style={{ color: p.score >= 70 ? "#10B981" : p.score >= 40 ? "#F59E0B" : "#EF4444" }}>
            {p.score}
          </span>
        </div>
      ))}
    </div>
  );
}

function AutoGenStatusBanner({ docs, user, onRefresh, onCheckout }) {
  // Solo aparece si el usuario completó onboarding y tiene docs auto en proceso
  const onboardingDone = user.onboarding_completo;
  const autoDocs = docs.filter((d) => d.auto_generado === true);
  const generandoCount = autoDocs.filter((d) => d.estado === "generando" || d.estado === "borrador").length;
  const generadosCount = autoDocs.filter((d) => d.estado === "generado").length;

  // Auto-refresh cada 6s mientras haya docs generándose
  useEffect(() => {
    if (generandoCount === 0) return;
    const id = setInterval(() => onRefresh?.(), 6000);
    return () => clearInterval(id);
  }, [generandoCount, onRefresh]);

  if (!onboardingDone) return null;

  // Si nunca se han disparado los autos (ej. cuenta antigua), invita a disparar
  if (autoDocs.length === 0) {
    const handleDispatch = async () => {
      try {
        await axios.post(`${API}/me/documents/auto-generate`);
        toast.success("Generando tus 3 documentos esenciales…");
        onRefresh?.();
      } catch (e) {
        const msg = e?.response?.data?.detail || "No pudimos disparar la generación";
        toast.error(typeof msg === "string" ? msg : "Error");
      }
    };
    return (
      <div
        data-testid="auto-gen-empty-banner"
        className="mb-5 rounded-2xl border border-blue-200 bg-blue-50/60 p-5 flex flex-wrap items-center justify-between gap-3"
      >
        <div>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-blue-700 mb-1">
            Aún no se generaron tus esenciales
          </p>
          <p className="text-sm text-slate-700">
            Generamos automáticamente Política de Privacidad, Cláusula de Consentimiento y Catálogo (RAT) con tus datos.
          </p>
        </div>
        <button
          data-testid="auto-gen-dispatch-btn"
          onClick={handleDispatch}
          className="h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" /> Generar ahora
        </button>
      </div>
    );
  }

  // Si hay docs generándose
  if (generandoCount > 0) {
    return (
      <div
        data-testid="auto-gen-progress-banner"
        className="mb-5 rounded-2xl border border-blue-200 bg-blue-50/60 p-5"
      >
        <div className="flex items-center gap-3 mb-2">
          <span className="live-dot inline-block" />
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-blue-700">
            Generando tus documentos esenciales · {generadosCount}/{autoDocs.length}
          </p>
        </div>
        <p className="text-sm text-slate-700">
          Claude está redactando tus documentos legales con tus datos del onboarding. Esto puede tardar 1-3 minutos.
        </p>
      </div>
    );
  }

  // Todos generados — solo Pro descarga
  if (!user.es_pro && generadosCount > 0) {
    return (
      <div
        data-testid="auto-gen-ready-banner"
        className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5 flex flex-wrap items-center justify-between gap-3"
      >
        <div>
          <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-emerald-700 mb-1">
            ✓ Tus {generadosCount} documentos están listos
          </p>
          <p className="text-sm text-slate-700">
            Activa Pro para descargar en PDF/Markdown y copiar el contenido completo.
          </p>
        </div>
        <button
          onClick={onCheckout}
          data-testid="auto-gen-unlock-cta"
          className="h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold inline-flex items-center gap-2"
        >
          <Lock className="w-4 h-4" /> Desbloquear con Pro
        </button>
      </div>
    );
  }

  return null;
}

function KpiTile({ label, value, tone = "info", hint }) {
  const tones = {
    live: {
      box: "border-emerald-200 bg-emerald-50/60",
      label: "text-emerald-700",
      value: "text-emerald-700",
      hint: "text-emerald-700/80",
      dot: "bg-emerald-500",
    },
    warn: {
      box: "border-amber-200 bg-amber-50/60",
      label: "text-amber-700",
      value: "text-amber-700",
      hint: "text-amber-700/80",
      dot: "bg-amber-500",
    },
    info: {
      box: "border-slate-200 bg-white",
      label: "text-slate-500",
      value: "text-slate-900",
      hint: "text-slate-500",
      dot: "bg-blue-500",
    },
    muted: {
      box: "border-slate-200 bg-slate-50",
      label: "text-slate-500",
      value: "text-slate-700",
      hint: "text-slate-500",
      dot: "bg-slate-400",
    },
  };
  const t = tones[tone] || tones.info;
  return (
    <div
      data-testid={`kpi-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
      className={`rounded-2xl border ${t.box} px-4 py-4 transition-shadow hover:shadow-sm`}
    >
      <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${t.label}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
        {label}
      </div>
      <div className={`mt-2 font-display text-2xl sm:text-3xl tabular-nums leading-none tracking-tight ${t.value}`}>
        {value}
      </div>
      {hint && <div className={`mt-1.5 text-[11px] ${t.hint}`}>{hint}</div>}
    </div>
  );
}
